


































































































import Vue from 'vue';
import { ELanguage, ESearchPostType, EUserRole } from '@ringana/configs';
import CookieHelper from '@/helpers/CookieHelper';
import { IApiSearchResult } from '@/common/interfaces/api.interface';
import EventListenerHelper from '../../helpers/EventListenerHelper';
import { ISearchboxComponent, ISearchboxRefs } from '../../common/interfaces/searchbox.interface';
import Articles from './resultcomponents/Articles.vue';
import CustomButton from '../layout/buttons/CustomButton.vue';
import FilterTextInput from '../layout/inputs/FilterTextInput.vue';
import KnowledgeBase from './resultcomponents/KnowledgeBase.vue';
import LocalStorageHelper from '../../helpers/LocalStorageHelper';
import Products from './resultcomponents/Products.vue';
import TIcons from '../../types/TIcons';
import config from '../../../config';

export default Vue.extend({
  name: `Searchbox`,
  components: {
    Articles,
    CustomButton,
    FilterTextInput,
    KnowledgeBase,
    Products,
  },
  data(): ISearchboxComponent & { $refs?: ISearchboxRefs } {
    return {
      debouncedFilter: null,
      eventListeners: [],
      icons: TIcons,
      isPartner: false,
      loading: {
        redirectLoading: false,
        resultLoading: false,
        showMoreLoading: false,
      },
      notificationVisible: false,
      pagination: {
        offset: 0,
        totalPosts: 0,
      },
      search: {
        isDirty: false,
        result: {
          articles: [],
          articlesFound: true,
          knowledgeBase: [],
          knowledgeBaseFound: true,
          products: [],
          productsFound: true,
        },
        searchString: ``,
        shouldClose: false,
        visible: false,
      },
      selectedLanguage: null,
      t: window.searchboxTexts,
    };
  },
  computed: {
    allPostsDisplayed(): boolean {
      // Handles edge case of initial search.
      if(this.search.isDirty) {
        return this.postsDisplayed >= this.pagination.totalPosts;
      }
      return false;
    },
    postsDisplayed(): number {
      return this.search.result.products.length + this.search.result.articles.length
        + this.search.result.knowledgeBase.length;
    },
    searchStringValid(): boolean {
      return this.search.searchString.length > 2;
    },
  },
  beforeDestroy(): void {
    this.teardownEventListeners();
  },
  mounted(): void {
    this.fetchCustomerRole();
    this.setUpEventListeners();
    this.getSelectedLanguage();
    this.debouncedFilter = window._.debounce(this.filter, 200);
  },
  methods: {
    assembleArticles(data: IApiSearchResult[], loadMore = false): void {
      const articles = data.find(item => {
        return item.postType === ESearchPostType.NEWS_ARTICLE.toString();
      });
      const blogs = data.find(item => {
        return item.postType === ESearchPostType.BLOG.toString();
      });
      this.search.result.articles = [
        ...loadMore ? this.search.result.articles : [],
        ...articles?.data ?? [],
        ...blogs?.data ?? [],
      ];
    },
    assembleKnowledgeBase(data: IApiSearchResult[], loadMore = false): void {
      const knowledgeBase = data.find(item => item.postType === ESearchPostType.KNOWLEDGE_BASE_ENTRY.toString());
      this.search.result.knowledgeBase = [
        ...loadMore ? this.search.result.knowledgeBase : [],
        ...knowledgeBase?.data ?? [],
      ];
    },
    assembleProducts(data: IApiSearchResult[], loadMore = false): void {
      const products = data.find(item => {
        return item.postType === ESearchPostType.PRODUCT.toString();
      });
      const partnerProducts = data.find(item => {
        return item.postType === ESearchPostType.PARTNER_PRODUCT.toString();
      });
      this.search.result.products = [
        ...loadMore ? this.search.result.products : [],
        ...products?.data ?? [],
        ...partnerProducts?.data ?? [],
      ];
    },
    blurSearchInput(): void {
      if(this.search.shouldClose) {
        this.showSearchbox(false);
      }
    },
    fetchCustomerRole(): void {
      const personalInfo = LocalStorageHelper.getPersonalInfo();
      if(personalInfo) {
        this.isPartner = personalInfo.isPartner;
      }
    },
    async filter(): Promise<void> {
      if(!this.searchStringValid) {
        return;
      }

      this.pagination.offset = 0;

      this.search.result.articlesFound = true;
      this.search.result.knowledgeBaseFound = true;
      this.search.result.productsFound = true;

      this.search.result.articles = [];
      this.search.result.knowledgeBase = [];
      this.search.result.products = [];

      this.loading.resultLoading = true;

      const response = await this.$apiService.getSearchData({
        data: {
          lang: this.selectedLanguage as ELanguage,
          limit: config.api.searchbox.resultLimit,
          showHiddenCategories: false,
          term: this.search.searchString,
        },
        params: {
          role: this.isPartner ? EUserRole.PARTNER : EUserRole.CUSTOMER,
        },
      });

      const { data } = response;

      this.assembleArticles(data);
      this.assembleKnowledgeBase(data);
      this.assembleProducts(data);

      this.pagination.totalPosts = data.map(item => item.count).reduce((a, b) => a + b, 0);

      this.search.result.articlesFound = this.search.result.articles.length > 0;
      this.search.result.knowledgeBaseFound = this.search.result.knowledgeBase.length > 0;
      this.search.result.productsFound = this.search.result.products.length > 0;

      this.loading.resultLoading = false;
      this.search.isDirty = true;
    },
    getSelectedLanguage(): void {
      this.selectedLanguage = CookieHelper.getLanguage() as ELanguage;

      if(!this.selectedLanguage) {
        this.selectedLanguage = ELanguage.DE;
      }
    },
    handleInputFocus(): void {
      const { input } = this.$refs.searchInput.$refs;
      input.focus();

      this.search.shouldClose = false;
    },
    initEventListeners(): void {
      this.eventListeners = [
        { callback: (): void => { this.toggleNotificationVisible(true); }, name: `rin-limiting-active` },
        { callback: (): void => { this.toggleNotificationVisible(false); }, name: `rin-limiting-not-active` },
        { callback: (): void => { this.showSearchbox(!this.search.visible); }, name: `rin-searchbox-searchbar-toggled` },
      ];
    },
    isButtonDisabled(key: string): boolean {
      switch(key) {
        case `searchbox-show-more`:
        case `searchbox-show-all`:
          return this.loading.showMoreLoading
            || this.loading.resultLoading
            || this.loading.redirectLoading
            || this.pagination.totalPosts === 0;
        default:
          return false;
      }
    },
    redirectToSearch(): void {
      if(this.searchStringValid) {
        this.loading.redirectLoading = true;
        window.location.href = `/?s=${this.search.searchString}&lang=${this.selectedLanguage}`;
      }
    },
    setUpEventListeners(): void {
      this.initEventListeners();

      this.eventListeners.forEach(eventListener => {
        document.addEventListener(eventListener.name, eventListener.callback);
      });
    },
    async showMore(): Promise<void> {
      this.pagination.offset += config.api.searchbox.offset;

      this.loading.showMoreLoading = true;

      const response = await this.$apiService.getSearchData({
        data: {
          lang: this.selectedLanguage as ELanguage,
          limit: config.api.searchbox.resultLimit,
          offset: this.pagination.offset,
          showHiddenCategories: false,
          term: this.search.searchString,
        },
        params: {
          role: this.isPartner ? EUserRole.PARTNER : EUserRole.CUSTOMER,
        },
      });

      const { data } = response;

      this.assembleArticles(data, true);
      this.assembleKnowledgeBase(data, true);
      this.assembleProducts(data, true);

      this.loading.showMoreLoading = false;
    },
    showSearchbox(show: boolean): void {
      this.search.searchString = ``;
      this.search.result.articles = [];
      this.search.result.knowledgeBase = [];
      this.search.result.products = [];

      this.search.visible = show;

      if(show) {
        this.$nextTick(() => {
          const { input } = this.$refs.searchInput.$refs;
          input.focus();
        });
      }
    },
    teardownEventListeners(): void {
      EventListenerHelper.teardownEventListeners(this.eventListeners);
    },
    toggleNotificationVisible(show: boolean): void {
      this.notificationVisible = show;
    },
  },
});
